import { useRef, useState, useEffect } from 'react';
import { ReportingContainerBaseConfig } from '../../../../../base';
import { SnippetElementController } from './snippetElementInterface';
import { SnippetElementContainerProps } from './snippetElementProps';
import { useDisclosure, useOutsideClick } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { assert } from '@varos/util-typescript';

export function createSnippetElementContainer(
    config: ReportingContainerBaseConfig,
    controller: SnippetElementController
): React.FC<SnippetElementContainerProps> {
    const { navigation } = config;
    return ({ as: View, ...containerProps }) => {
        const initialValue = containerProps.value;
        const containerRef = useRef<HTMLDivElement>(null);
        const [value, setValue] = useState(initialValue);
        const searchParams = useSearchParams();
        const disclosure = {
            popover: useDisclosure({}),
            fullscreen: useDisclosure({
                onOpen() {
                    assert(value, 'value is required');
                    navigation.snippet.view.open({
                        search: searchParams,
                        snippetId: value.id,
                    });
                },
            }),
        };

        useEffect(() => {
            setValue(containerProps.value);
        }, [containerProps.value]);

        useOutsideClick({
            ref: containerRef,
            handler: () => {
                disclosure.popover.onClose();
            },
        });

        const props = controller.useProps(
            {
                property: containerProps.property,
            },
            {
                disclosure,
                containerRef: containerRef,
                field: {
                    isDirty: value !== initialValue,
                    value: value,
                    onChange(value) {
                        setValue(value);
                        containerProps.onChange(value);
                        disclosure.popover.onClose();
                    },
                },
                async onSubmit(value) {
                    setValue(value ? { id: value } : null);
                    containerProps.onChange(value ? { id: value } : null);
                    disclosure.popover.onClose();
                },
            }
        );

        return <View {...props} />;
    };
}
